/**
 * OceanSimulation.ts — orchestrates two FFT cascades and exposes their outputs
 * to the WaterRenderPass.
 *
 * Implements RenderPass so it sits in the RenderPipeline before the water
 * geometry pass.
 *
 *   Cascade 1: L = 64 m, FFT 256 → pixel 0.25 m (medium / far waves)
 *   Cascade 2: L = 8  m, FFT 256 → pixel 3 cm   (capillary / near waves)
 *
 * Wind is read from `frame.state` each frame; if direction or speed changed,
 * spectrum_init is re-run for both cascades (debounced trivially: only when
 * values differ from the cached ones).
 */

import { OceanCascade } from "./OceanCascade";
import type { FrameContext, RenderPass } from "../../rendering/RenderPass";

export class OceanSimulation implements RenderPass {
  readonly name = "OceanSimulation";

  readonly cascade1: OceanCascade;
  readonly cascade2: OceanCascade;

  private lastWindDirDeg = NaN;
  private lastWindSpeed = NaN;
  private lastWindExposure = NaN;
  private lastMaxWaveAmpScale = NaN;

  constructor(private device: GPUDevice) {
    this.cascade1 = new OceanCascade(device, {
      L: 64.0,
      amplitude: 18500.0,
      directionality: 4,
      smallWaveCutoff: 0.02,
      depthM: 4.0,
      fetchM: 900.0,
      gammaPeak: 2.2,
      seed: 0xc0ffee01,
      label: "C1-64m",
    });
    this.cascade2 = new OceanCascade(device, {
      L: 8.0,
      amplitude: 2300.0,
      directionality: 6,
      smallWaveCutoff: 0.002,
      depthM: 3.0,
      fetchM: 300.0,
      gammaPeak: 1.8,
      seed: 0xfacade02,
      label: "C2-8m",
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const { state, time } = frame;

    // Re-initialize h0 if wind changed (or on first frame).
    const exposure = state.windExposure ?? 1.0;
    const maxAmp = state.maxWaveAmpScale ?? 2.0;
    if (
      state.windDirectionDeg !== this.lastWindDirDeg ||
      state.windSpeed !== this.lastWindSpeed ||
      exposure !== this.lastWindExposure ||
      maxAmp !== this.lastMaxWaveAmpScale
    ) {
      this.lastWindDirDeg = state.windDirectionDeg;
      this.lastWindSpeed = state.windSpeed;
      this.lastWindExposure = exposure;
      this.lastMaxWaveAmpScale = maxAmp;

      // Meteorological azimuth → vector "wind blows TOWARD" in xz-plane.
      // Convention: 0° = wind FROM north (so blowing toward +Z if north = -Z).
      // We use east = +X, north = -Z. Wind FROM azimuth a means the wind vector
      // points opposite to the source direction.
      const a = (state.windDirectionDeg * Math.PI) / 180;
      // Direction vector pointing TOWARD where wind goes:
      const wx = -Math.sin(a);
      const wz = Math.cos(a);
      // Apply the terrain's wind-exposure factor: sheltered bays grow
      // smaller waves at the same forecast wind. Then keep a small minimum
      // effective wind so the spectrum never goes fully dead at near-calm.
      const exposedWindSpeed = state.windSpeed * exposure;
      const effectiveU = Math.max(exposedWindSpeed, 3.5);
      // Wind-energy gain for lake scale. Slightly sub-quadratic to avoid
      // storm-like seas at moderate winds. Capped by the active terrain's
      // max_wave_amp_scale so small enclosed waters never build big waves.
      const rawAmplitudeScale = Math.max(0.12, (exposedWindSpeed / 6) ** 1.55);
      const amplitudeScale = Math.min(maxAmp, Math.min(2.0, rawAmplitudeScale));
      const wind = {
        windDir: [wx, wz] as [number, number],
        windSpeed: effectiveU,
        amplitudeScale,
      };
      // Submit the init dispatch on its own encoder so the storage-texture
      // writes to h0Tex are flushed to the queue before the per-frame
      // update pass (which samples h0Tex) is recorded. Recording both
      // passes in the same encoder produced an inter-pass sync issue
      // where update read a zero h0 on the very first frame, leaving the
      // water mirror-flat until the user nudged the wind slider.
      const initEncoder = this.device.createCommandEncoder({
        label: "OceanSimulation.init",
      });
      this.cascade1.encodeInit(initEncoder, wind);
      this.cascade2.encodeInit(initEncoder, wind);
      this.device.queue.submit([initEncoder.finish()]);
    }

    this.cascade1.encodeUpdate(encoder, time);
    this.cascade2.encodeUpdate(encoder, time);
  }

  destroy(): void {
    this.cascade1.destroy();
    this.cascade2.destroy();
  }
}
